import { ActionsDropdown, CodeDisplay, DataTable, Icon, Tooltip } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import React, { useEffect, useMemo, useRef } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import type { ValueExpr } from '@aidkitorg/types/lib/survey';
import { nameForColumn } from '@aidkitorg/types/lib/translation/expr_to_sql';
import { useLocalizedStrings } from '../../../Localization';
import { snakeToEnglish } from '../../../Util';

export type QueueApplicantRow = {
  uid: string;
  legal_name?: string | null;
  assignee?: string | null;
  /** Resolved display name for the assignee UID, populated server-side. */
  assignee_name?: string | null;
  assigned_at?: string | null;
  completed_at?: string | null;
  state?: string | null;
  additionalColumns?: Record<string, unknown>;
};

interface QueueApplicantsTableProps {
  rows: QueueApplicantRow[];
  loading?: boolean;
  startSection?: string;
  /** Show the Assignee / Assigned At columns (relevant for assigned/completed tabs). */
  showAssignment?: boolean;
  /** When provided, renders a Claim action column that claims the given applicant. */
  onClaim?: (uid: string) => void;
  additionalColumns?: ValueExpr[];
}

const formatDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const EMPTY_ADDITIONAL_COLUMNS: ValueExpr[] = [];

export const QueueApplicantsTable: React.FC<QueueApplicantsTableProps> = ({
  rows,
  loading = false,
  startSection = 'review',
  showAssignment = false,
  onClaim,
  additionalColumns = EMPTY_ADDITIONAL_COLUMNS,
}) => {
  const L = useLocalizedStrings();
  const history = useHistory();

  // Held in a ref and kept out of the columns memo below: an unmemoized onClaim from the
  // parent would rebuild the column defs every render, and TanStack renders each cell via
  // createElement(columnDef.cell), so a new cell function is a new element type and React
  // remounts the cell — closing any open Actions dropdown mid-click.
  const onClaimRef = useRef(onClaim);
  useEffect(() => { onClaimRef.current = onClaim; });

  // Whether claiming is enabled *does* change the column set (dropdown vs eye icon); the
  // callback's identity does not.
  const canClaim = !!onClaim;

  const normalizedSection = startSection.startsWith('/') ? startSection.slice(1) : startSection;

  const columns: ColumnDef<QueueApplicantRow>[] = useMemo(() => {
    const cols: ColumnDef<QueueApplicantRow>[] = [
      {
        id: 'uid',
        accessorKey: 'uid',
        header: L.admin_panel.reviews.uid,
        enableSorting: true,
        enablePinning: true,
        cell: ({ row }) => (
          <CodeDisplay displayMode="inline" code={String(row.original.uid)} copyable={true} className="text-xs" />
        ),
      },
      {
        accessorKey: 'legal_name',
        header: L.admin_panel.reviews.name,
        enableSorting: true,
        cell: ({ row }) => <span>{row.original.legal_name || '--'}</span>,
      },
    ];

    if (showAssignment) {
      cols.push(
        {
          accessorKey: 'assignee',
          header: L.admin_panel.reviews.assignee,
          enableSorting: true,
          cell: ({ row }) => (
            <span className="text-sm">{row.original.assignee_name || row.original.assignee || '--'}</span>
          ),
        },
        {
          accessorKey: 'assigned_at',
          header: L.admin_panel.reviews.assigned_at,
          enableSorting: true,
          cell: ({ row }) => {
            const formatted = formatDate(row.original.assigned_at);
            return formatted ? <span className="text-sm">{formatted}</span> : <span className="text-gray-400">--</span>;
          },
        },
      );
    }

    for (const expr of additionalColumns) {
      const key = nameForColumn(expr);
      if (!key) continue;
      cols.push({
        id: key,
        accessorFn: (row) => row.additionalColumns?.[key],
        header: snakeToEnglish(key),
        enableSorting: true,
        cell: ({ row }) => {
          const value = row.original.additionalColumns?.[key];
          return <span className="text-sm">{value === null || value === undefined || value === '' ||  value === 'NaN' ? '--' : String(value)}</span>;
        },
      });
    }

    cols.push({
      id: 'actions',
      header: canClaim ? L.component_library.data_table.actions : L.admin_panel.reviews.view,
      enableSorting: false,
      enablePinning: true,
      size: 10,
      cell: ({ row }) => {
        // In Review / Completed: no claim action, so show a simple eye-icon link.
        if (!canClaim) {
          return (
            <Tooltip text={L.admin_panel.reviews.view_tooltip}>
              <NavLink
                to={`/admin-panel/a/${row.original.uid}/${normalizedSection}`}
                className="p-1 hover:bg-gray-100 rounded transition-colors inline-flex text-gray-700"
                aria-label={L.admin_panel.reviews.view_tooltip}
              >
                <Icon name="EyeIcon" type="solid" size="md" role="decorative" />
              </NavLink>
            </Tooltip>
          );
        }

        // In Queue: Claim is the primary action, so list it above View.
        return (
          <ActionsDropdown
            item={row.original}
            anchor="bottom start"
            actions={[
              {
                label: L.admin_panel.reviews.claim,
                icon: 'UserGroupIcon',
                onClick: (item: QueueApplicantRow) => onClaimRef.current?.(item.uid),
              },
              {
                label: L.admin_panel.reviews.view,
                icon: 'EyeIcon',
                onClick: (item) => history.push(`/admin-panel/a/${item.uid}/${normalizedSection}`),
              },
            ]}
          />
        );
      },
    });

    return cols;
  }, [L, showAssignment, normalizedSection, canClaim, history, additionalColumns]);

  return (
    <DataTable
      columns={columns}
      data={rows}
      enableSearch
      searchLabel={L.admin_panel.search_placeholder}
      noDataMessage={L.admin_panel.reviews.no_applicants_assigned}
      loading={loading}
      showPagination
      pageSize={10}
      resizableColumns
      stickyHeader
      tableHeight="fill-container"
      columnPinningState={{ left: ['uid'], right: ['actions'] }}
    />
  );
};
